<div class="flex h-full flex-col gap-4 p-6">
  <div class="flex flex-col items-center gap-2">
    <div class="typo-h1 text-text-primary">{{ 'charting_treatment_plan_review_title' | translate }}</div>
    <div class="typo-h4-light whitespace-pre-line text-center text-text-tertiary">
      {{ 'charting_treatment_plan_review_subtitle' | translate }}
    </div>
  </div>
  <div class="grid flex-1 auto-rows-min grid-cols-[240px,_1fr] gap-x-1 gap-y-12 overflow-auto bg-background-white p-10">
    <div class="typo-h3-medium text-text-tertiary">{{ 'charting_treatment_plan_review_patient_name' | translate }}</div>
    <div class="typo-h4-semibold uppercase tracking-[10%]">{{ dialogData.patientName }}</div>
    <div class="typo-h3-medium text-text-tertiary">{{ 'charting_treatment_plan_review_treatment_consents' | translate }}</div>
    <div class="flex flex-col gap-6">
      @for (consent of dialogData.treatmentConsents; track consent.dentalCode) {
        <div class="flex flex-col gap-2">
          <div class="typo-h3-semibold text-text-primary">{{ consent.treatmentName }}</div>
          <p class="typo-h4-light-line-height-20">{{ consent.description }}</p>
          <a target="_blank" rel="noopener noreferrer" [href]="'http://' + consent.consentUrl" class="typo-h3-semibold text-primary-blue">
            {{ 'charting_treatment_plan_review_treatment_consent_see_full' | translate }}
          </a>
        </div>
      }
      <div class="flex items-center gap-3 rounded-3xl bg-background-light-blue p-4">
        <lib-checkbox [formControl]="consentAcceptedForm" />
        <div class="typo-h4-medium-line-height-20 text-primary-blue">
          {{ 'charting_treatment_plan_review_treatment_consents_check' | translate }}
        </div>
      </div>
    </div>
    <div class="typo-h3-medium text-text-tertiary">{{ 'charting_treatment_plan_review_treatment_plan' | translate }}</div>
    <div class="grid grid-cols-[300fr_56fr_70fr_265fr_77fr] gap-x-4 gap-y-5">
      <div class="spaced-dot-border typo-h5-light col-start-1 col-end-[-1] grid pb-3 text-text-tertiary grid-cols-subgrid">
        <div>{{ 'charting_treatment_plan_review_treatment_plan_treatment' | translate }}</div>
        <div>{{ 'charting_treatment_plan_review_treatment_plan_tooth' | translate }}</div>
        <div>{{ 'charting_treatment_plan_review_treatment_plan_service' | translate }}</div>
        <div>{{ 'charting_treatment_plan_review_treatment_plan_provider' | translate }}</div>
        <div>{{ 'charting_treatment_plan_review_treatment_plan_estimated_cost' | translate }}</div>
      </div>
      @for (treatment of dialogData.treatments; track treatment.dentalCode) {
        <div class="spaced-dot-border col-start-1 col-end-[-1] grid items-center gap-y-3 pb-5 grid-cols-subgrid">
          <div class="typo-h4-semibold flex flex-col text-text-primary">
            <div>{{ treatment.dentalCode }}</div>
            <div>{{ treatment.treatmentName }}</div>
          </div>
          <div class="typo-h4-semibold text-text-primary">{{ treatment.toothNumbers }}</div>
          <div class="typo-h4-semibold text-text-primary">{{ treatment.surface }}</div>
          <div class="typo-h4-light text-text-tertiary">{{ treatment.providerName }}</div>
          <div class="typo-h4-light text-right text-text-tertiary">{{ treatment.cost | currency: 'USD' : 'symbol' : '1.0-0' }}</div>
          <div class="typo-h4-light-line-height-20 col-start-1 col-end-[-1] text-text-secondary">{{ treatment.treatmentDescription }}</div>
        </div>
      }
    </div>
    <div class="typo-h3-medium text-text-tertiary">{{ 'charting_treatment_plan_review_treatment_review' | translate }}</div>
    <div class="flex flex-col gap-8">
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <div class="flex">
            <div class="typo-h4-light shrink-0 text-text-secondary">
              {{ 'charting_treatment_plan_review_treatment_review_subtotal_of_services' | translate }}
            </div>
            <div class="spaced-dot-border flex-1"></div>
            <div class="typo-h4-medium shrink-0 text-text-primary">{{ subTotal | currency: 'USD' : 'symbol' : '1.2-2' }}</div>
          </div>
          <div class="flex">
            <div class="typo-h4-light shrink-0 text-text-secondary">
              {{ 'charting_treatment_plan_review_treatment_review_insurance_estimate' | translate }}
            </div>
            <div class="spaced-dot-border flex-1"></div>
            <div class="typo-h4-medium shrink-0 text-states-red">({{ insuranceEstimate | currency: 'USD' : 'symbol' : '1.2-2' }})</div>
          </div>
          <div class="flex">
            <div class="typo-h4-light shrink-0 text-text-secondary">
              {{ 'charting_treatment_plan_review_treatment_review_discount' | translate }}
            </div>
            <div class="spaced-dot-border flex-1"></div>
            <div class="typo-h4-medium shrink-0 text-states-red">{{ discount | currency: 'USD' : 'symbol' : '1.2-2' }}</div>
          </div>
          <div class="mt-1 flex">
            <div class="typo-h3-semibold shrink-0 text-text-primary">
              {{ 'charting_treatment_plan_review_treatment_review_patient_estimated_total' | translate }}
            </div>
            <div class="spaced-dot-border flex-1"></div>
            <div class="typo-h3-semibold shrink-0 text-text-primary">{{ total | currency: 'USD' : 'symbol' : '1.2-2' }}</div>
          </div>
        </div>
        <div class="typo-h4-medium-line-height-20 flex items-center gap-3 rounded-3xl bg-background-light-blue p-4 text-primary-blue">
          {{ 'charting_treatment_plan_review_treatment_review_hint_text' | translate }}
        </div>
      </div>
      <div class="flex flex-col gap-3">
        <div class="typo-h4-light text-text-secondary">{{ today | date: 'mediumDate' }}</div>
        <div class="flex flex-col gap-2.5">
          <lib-sign-box (isSigned)="onSignChange($event)" />
          <div class="typo-h5-light text-text-tertiary">{{ 'charting_treatment_plan_review_treatment_review_sign_hint' | translate }}</div>
        </div>
      </div>
    </div>
  </div>
  <div class="flex justify-end gap-6">
    <button class="btn-text" (click)="onCancelClick()">{{ 'charting_treatment_plan_review_treatment_cancel' | translate }}</button>
    <button class="btn-primary py-3 shadow-none" [disabled]="consentAcceptedForm.invalid || !isSigned" (click)="onAcceptClick()">
      {{ 'charting_treatment_plan_review_treatment_accept' | translate }}
    </button>
  </div>
</div>
